import angular from 'angular';
import dateComponent from './date-component';
import i18n from '../i18n';
import angularBootstrap from 'angular-ui-bootstrap';
import moment from 'moment-timezone';
import time from '../time';
import autocomplete from '../autocomplete';
import timezonesService from './timezones-service';
import timezoneComponent from './timezone-component';
import permittedTimezones from './permitted-timezones.json';

/**
 * @ngdoc module
 * @name akamai.components.date
 *
 * @guideline https://design.akamai.com/components/date-picker/
 *
 * @description
 * Date is an interactive calendar component that allows users to select a date
 * from a month view. Users have the ability to clear a selected date.
 *
 * Timezones and Model Behavior
 *
 * To allow for the most flexibility, the model for the date component is a simple native
 * javascript date object. Native javascript date objects always use the local timezone. If the
 * timezone attribute is supplied to the component, the component will allow users to change the
 * timezone. It will figure out the offset between the selected timezone and local timezone and
 * apply/update the model with the correct offset.
 *
 * Example:
 *
 * 1.) The user is in EST and has selected Jan 1, 12:00am EST in a date component
 * 2.) The user changes just the timezone to PST in the same date component. (The date
 *     component now reads Jan 1, 12:00am PST)
 * 3.) Since native javascript date objects are always in local time (in this example EST),
 *     the model needs to be updated accordingly. The following question needs to be answered:
 *     "Jan 1, 12:00am PST is what date/time in EST? - the answer is Jan 1, 3:00am EST"
 * 4.) The component still reads Jan 1, 12:00am PST (view-model) but the ngModel is now
 *     Jan 1, 3:00am EST
 *
 * A timezone can be initially supplied to the component via ng-model-options (see below). The
 * list of supported timezones can be found in permitted-timezones.json within akamai-core.
 *
 * Lastly, remember to always persist dates in UTC. These dates will be localized when
 * javascript date object representations are created from them. The date component is designed
 * to work with these localized javascript date objects.
 *
 * @example day.html
 * <akam-date
 *   mode="day"
 *   ng-model="..."
 *   ng-model-options="..."
 *   min="..."
 *   max="..."
 *   ng-change="..."
 *   format="..."
 *   time
 *   timezone
 *   no-meridian
 *   is-disabled="..."
 *   is-readonly="...">
 * </akam-date>
 *
 * @example month.html
 * <akam-date
 *  mode="month"
 *  ng-model="..."
 *  min="..."
 *  max="..."
 *  ng-change="..."
 *  format="..."
 *  clearable>
 * </akam-date>
 */
export default angular.module('akamai.components.date', [
  angularBootstrap,
  i18n.name,
  time.name,
  autocomplete.name
])
  .service('timezonesService', timezonesService)
  .component('akamTimezone', timezoneComponent)
  /**
   * @ngdoc directive
   *
   * @name akamDate
   *
   * @description Creates a date control.
   *
   * @restrict E
   *
   * @param {Date} ng-model The date value. Must be a valid JavaScript `Date` object.
   *
   * @param {string} ng-model-options.timezone A timezone to use for initial rendering of the
   * control. Possible timezone strings can be found in the `timezones` service
   * (i.e. `timezones.data`).
   *
   * @param {string|TranslateKey} [placeholder] The placeholder text.
   *
   * @param {string} [mode="day"] Determines the display mode. Can be either `day` or `month`.
   *
   * @param {Date|string} [min] The earliest date users can select. Dates before this point are
   * disabled.
   *
   * @param {Date|string} [max] The latest date users can select. Dates after this point are
   * disabled.
   *
   * @param {number} [hour-step=1] If time is present, this value is used to increment/decrement
   * the hour value.
   *
   * @param {number} [minute-step=15] If time is present, this value used to increment/decrement
   * the minute value.
   *
   * @param {string} [format="EEE, MMM dd, yyyy" | format="MMM yyyy"] An angular-compatible date
   * format. By default, `"EEE, MMM dd, yyyy"` will be used when in `day` mode. `"MMM yyyy"` will be
   * used when in `month` mode.
   *
   * @param {boolean} [is-disabled=false] If disabled, no user interaction will be possible.
   *
   * @param {boolean} [is-readonly=false] If readonly, no user interaction will be possible but the
   * text will be easier to read.
   *
   * @param {*} [clearable] If present, the date will be clearable.
   *
   * @param {*} [time] If present, a time control will be displayed.
   *
   * @param {*} [timezone] If present, both a time and timezone controls will be displayed.
   *
   * @param {*} [no-meridian] If present, the time will not have a meridian and will be rendered in
   * military time.
   *
   * @param {Function} dateDisabled An optional expression to disable visible options based on
   * passing an object with date and current mode properties
   */
  .component('akamDate', dateComponent)
  .constant('permittedTimezones', permittedTimezones)
  .value('moment', moment);
